import React, { useState } from 'react';
import { validateActivityPayload, GAME_MODE_CATALOG, type ArenaPayload } from '@interactive-room';
import { emptyArenaPayload } from '../emptyPayloads';
import { StudioLiveShell } from '../components/StudioLiveShell';
import { createDraftId, saveActivityDraft, type ActivityDraft } from '../activityStorage';
import { resolveStudioLaunchDefaults } from '../studioLaunch';
import { generateSubjectAwareActivityDraft } from '../studioActivityGeneration';
import type { StudioLiveEditorProps } from '../types/editor.types';

export function ArenaLiveEditor({
  draftId,
  embedded,
  onSaved,
  launchSubjectId,
  launchPresetId,
}: StudioLiveEditorProps) {
  const launch = resolveStudioLaunchDefaults(emptyArenaPayload, {
    draftId,
    launchSubjectId,
    launchPresetId,
  });
  const [payload, setPayload] = useState<ArenaPayload>(() => launch.payload);
  const [subjectTag, setSubjectTag] = useState(() => launch.subjectTag);
  const [qIndex, setQIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [savedId] = useState(draftId ?? createDraftId());

  const meta = GAME_MODE_CATALOG.arena;
  const question = payload.questions[qIndex];

  const updateQuestion = (patch: Record<string, unknown>) => {
    setPayload((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === qIndex ? { ...q, ...patch } : q)),
    }));
  };

  const updateOption = (optIndex: number, text: string) => {
    if (question.kind !== 'multiple-choice') return;
    const options = [...question.options];
    options[optIndex] = text;
    updateQuestion({ options });
  };

  const addOption = () => {
    if (question.kind !== 'multiple-choice') return;
    updateQuestion({ options: [...question.options, ''] });
  };

  const toggleCorrect = (optIndex: number) => {
    if (question.kind !== 'multiple-choice') return;
    updateQuestion({ correctIndex: optIndex });
  };

  const addQuestion = () => {
    setPayload((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          id: `q_${prev.questions.length + 1}`,
          kind: 'multiple-choice' as const,
          prompt: '',
          options: ['', ''],
          correctIndex: 0,
          points: 100,
          timeLimitSec: prev.settings?.defaultTimeLimitSec ?? 30,
        },
      ],
    }));
    setQIndex(payload.questions.length);
  };

  const handleAiBuild = async (prompt: string) => {
    setAiLoading(true);
    setError(null);
    setNotice(null);
    try {
      const { draft, notice: aiNotice } = await generateSubjectAwareActivityDraft({
        mode: 'arena',
        subjectId: launchSubjectId,
        subjectTag: subjectTag.trim() || undefined,
        tuning: prompt,
        draftId: savedId,
        persist: false,
      });
      setPayload(draft.payload as ArenaPayload);
      if (draft.subjectTag) setSubjectTag(draft.subjectTag);
      setQIndex(0);
      if (aiNotice) setNotice(aiNotice);
    } catch (e) {
      setError(
        e instanceof Error && e.message === 'subject_context_required'
          ? 'Selecciona una asignatura desde Herramientas para generar con IA.'
          : e instanceof Error
            ? e.message
            : 'No se pudo generar con IA',
      );
    } finally {
      setAiLoading(false);
    }
  };

  const save = () => {
    try {
      validateActivityPayload('arena', payload);
      setError(null);
      onSaved?.(
        saveActivityDraft({
          id: savedId,
          mode: 'arena',
          title: payload.title,
          subjectTag: subjectTag.trim() || 'general',
          payload,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    }
  };

  if (!question) return null;

  const sidebar = (
    <div className="space-y-2">
      {payload.questions.map((q, i) => (
        <button
          key={q.id}
          type="button"
          className="block w-full rounded-lg px-2 py-1.5 text-left text-xs font-bold"
          style={{
            background: i === qIndex ? 'var(--sala-accent-neon)' : 'var(--plugin-btn-bg)',
            color: i === qIndex ? '#0a1628' : 'inherit',
          }}
          onClick={() => setQIndex(i)}
        >
          Pregunta {i + 1}
        </button>
      ))}
      <button type="button" className="explora-toolbar-btn w-full text-xs" onClick={addQuestion}>
        + Pregunta
      </button>
    </div>
  );

  return (
    <StudioLiveShell
      embedded={embedded}
      layout={embedded ? 'split' : 'default'}
      error={error}
      subjectTag={subjectTag}
      onSubjectTagChange={setSubjectTag}
      onSave={save}
      aiActivityLabel="cuestionario"
      onAiBuild={handleAiBuild}
      aiLoading={aiLoading}
      sidebar={sidebar}
      sidebarTitle="Preguntas"
    >
      {notice && (
        <p className="mx-4 mt-2 rounded-lg border border-amber-500/30 bg-amber-950/20 px-3 py-2 text-[10px] text-amber-200/90">
          {notice}
        </p>
      )}
      {aiLoading && (
        <p className="mx-4 mt-2 text-center text-xs text-[var(--plugin-muted)]">Generando cuestionario…</p>
      )}
      <header className="plugin-live-toolbar">
        <input
          className="canvas-inline-input canvas-inline-input--title min-w-0 flex-1 bg-transparent font-display text-base font-bold"
          value={payload.title}
          onChange={(e) => setPayload((p) => ({ ...p, title: e.target.value }))}
          placeholder="Título"
        />
        <span className="explora-chip">{meta.displayName}</span>
      </header>

      <div className="plugin-live-body flex min-h-0 flex-1 flex-col justify-center p-4 sm:p-6">
        <p className="mb-2 text-center text-[10px] font-bold uppercase tracking-widest opacity-50">
          Pregunta {qIndex + 1} · doble clic marca la correcta
        </p>
        <textarea
          className="canvas-inline-input mb-6 w-full resize-none bg-transparent text-center font-display text-xl font-bold leading-snug sm:text-2xl"
          rows={2}
          value={question.prompt}
          placeholder="Consigna…"
          onChange={(e) => updateQuestion({ prompt: e.target.value })}
        />

        {question.kind === 'multiple-choice' && (
          <div className="grid gap-2 sm:grid-cols-2">
            {question.options.map((opt, i) => {
              const correct = question.correctIndex === i;
              return (
                <button
                  key={i}
                  type="button"
                  className="arena-option-tile rounded-xl px-3 py-4 text-left"
                  data-correct={correct}
                  onDoubleClick={() => toggleCorrect(i)}
                >
                  <input
                    className="canvas-inline-input w-full bg-transparent text-center font-display text-sm font-bold"
                    value={opt}
                    placeholder={`Opción ${String.fromCharCode(65 + i)}`}
                    onChange={(e) => updateOption(i, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {correct && (
                    <span className="mt-1 block text-center text-[10px] font-bold uppercase text-emerald-500">
                      ✓ Correcta
                    </span>
                  )}
                </button>
              );
            })}
            <button
              type="button"
              className="rounded-xl border border-dashed border-[var(--plugin-border)] py-4 text-xs font-bold opacity-60"
              onClick={addOption}
            >
              + Opción
            </button>
          </div>
        )}
      </div>
    </StudioLiveShell>
  );
}
